/**
 * Bundle picker for Twin Kits with size selector and resizing options.
 *
 * Defensive version: prevents the customer from submitting the form without
 * completing the selectors, ensuring `_bundle_component_1` and
 * `_bundle_component_2` ALWAYS arrive at the cart with a valid variant ID.
 *
 * Compatible with themes that use Alpine.js (like this one). Alpine
 * reactively controls `disabled` on the button, so we DO NOT disable it via
 * the attribute (it would be reset on the next render). Instead we use:
 *   - CSS class on the wrapper (rendered server-side → no FOUC).
 *   - Capture-phase event listeners (run before Alpine's bubble-phase ones).
 *
 * Slot allocation (no JS name overrides — Liquid sets the right slots):
 *   items[1] → engraving service (set by engraving-form.liquid)
 *   items[2] → resizing service for bundle component 1
 *   items[3] → resizing service for bundle component 2
 */

document.addEventListener('DOMContentLoaded', function () {
  const container = document.querySelector('[data-bundle-container]');
  if (!container) return;

  // Inject CSS that visually signals the button is not ready. The click is
  // blocked by JS event listeners (not CSS pointer-events) so we can show
  // an alert when the customer tries to add to cart without selections.
  if (!document.getElementById('bundle-picker-defensive-styles')) {
    const style = document.createElement('style');
    style.id = 'bundle-picker-defensive-styles';
    style.textContent = `
      .add-to-cart.bundle-not-ready,
      [name="add"].bundle-not-ready,
      button[type="submit"].bundle-not-ready {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  // Find the product form. Hidden inputs reference form="product-form-{section.id}".
  const sectionId = container.dataset.sectionId;
  const productForm =
    document.getElementById(`product-form-${sectionId}`) ||
    container.closest('form');

  // Find the "Add to cart" button. We try several selectors because this
  // theme uses class="add-to-cart" instead of the standard name="add".
  const addToCartBtn =
    productForm?.querySelector('button.add-to-cart') ||
    productForm?.querySelector('[name="add"]') ||
    productForm?.querySelector('button[type="submit"]') ||
    document.querySelector('button.add-to-cart');

  /**
   * State: the wrapper has the `bundle-incomplete` class when there are no
   * complete selections. CSS in the Liquid snippet handles the visual
   * styling (applied from the initial HTML → no FOUC). We also toggle a
   * fallback class on the button itself for compatibility.
   */
  function refreshSubmitState() {
    const id1 = container.querySelector('[data-id-input-1]')?.value?.trim();
    const id2 = container.querySelector('[data-id-input-2]')?.value?.trim();
    const ready = !!(id1 && id2);

    container.classList.toggle('bundle-incomplete', !ready);

    if (addToCartBtn) {
      addToCartBtn.classList.toggle('bundle-not-ready', !ready);
    }
  }

  // Initial state: the wrapper already comes with `bundle-incomplete` from
  // the Liquid, so this is just a sanity refresh in case JS detects more.
  refreshSubmitState();

  /**
   * Validation: are both bundle components filled with a value?
   */
  function bundleIsComplete() {
    const id1 = container.querySelector('[data-id-input-1]')?.value?.trim();
    const id2 = container.querySelector('[data-id-input-2]')?.value?.trim();
    return !!(id1 && id2);
  }

  /**
   * Block the event if the bundle is not complete. Used for click and submit.
   */
  function guardBundleEvent(e) {
    if (!bundleIsComplete()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('Please select sizes for both items in the bundle before adding to cart.');
      return false;
    }
  }

  // Defense #1: intercept the button's direct click in capture phase.
  // This runs before Alpine.js (which uses bubble-phase listeners).
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', guardBundleEvent, { capture: true });
  }

  // Defense #2: intercept form submit (in case something bypasses the
  // button click, e.g. Enter key on an input or a programmatic submit).
  if (productForm) {
    productForm.addEventListener('submit', guardBundleEvent, { capture: true });
  }

  /**
   * Updates the state of a bundle item with the customer's selection.
   *
   * @param {string} itemId      "1" or "2"
   * @param {string} displaySize What's shown to the customer (may include resizing)
   * @param {string} baseSize    Variant base size (without resizing)
   * @param {boolean} isResizing Whether the customer chose a resizing option
   */
  function updateItemState(itemId, displaySize, baseSize, isResizing) {
    const textInput = container.querySelector(`[data-text-input-${itemId}]`);
    const idInput = container.querySelector(`[data-id-input-${itemId}]`);
    const resBox = container.querySelector(
      `.resizing-info-box[data-item-index="${itemId}"]`
    );
    const variantMap = window[`item${itemId}Data`]?.variants || {};

    if (textInput) textInput.value = baseSize;

    // Set the variant ID. If the lookup fails, log and explicitly clear
    // the input (don't keep a stale value that masks the problem).
    if (idInput) {
      const variantId = variantMap[baseSize];
      if (variantId) {
        idInput.value = variantId.toString().trim();
      } else {
        idInput.value = '';
        console.error(
          `[BundlePicker] No variant found for size "${baseSize}" in item ${itemId}. ` +
            `Available variants: ${Object.keys(variantMap).join(', ')}. ` +
            `Likely mismatch between option value and variant.title.`
        );
      }
    }

    const combinedAttr = container.querySelector(
      `[data-res-combined-attr-${itemId}]`
    );

    // Customer-visible resizing summary on the twin kit parent product.
    // Enabled/populated when a resizing option is chosen; cleared and
    // disabled when the customer picks a base size (no resizing).
    const visibleProp = container.querySelector(`[data-res-visible-prop-${itemId}]`);

    if (resBox) {
      const inputs = resBox.querySelectorAll('input');
      const baseSizeProp = resBox.querySelector(
        `[data-res-base-size-prop-${itemId}]`
      );
      const sizeProp = resBox.querySelector(`[data-res-size-prop-${itemId}]`);

      if (isResizing) {
        resBox.style.display = 'block';
        inputs.forEach((i) => (i.disabled = false));

        const detailsString = `Original Size: ${baseSize} -> New Size: ${displaySize}`;

        // Set values only — the input `name` attributes are already
        // correct from Liquid (items[2] for component 1, items[3] for
        // component 2). No name overrides needed here.
        if (baseSizeProp) baseSizeProp.value = baseSize;
        if (sizeProp) sizeProp.value = displaySize;

        // Cart attribute: apply the per-item base name from Liquid,
        // which is `attributes[<component> - Resizing (Twin Kit) (#__PID__)]`.
        // theme-events.js later replaces __PID__ with the purchase ID.
        if (combinedAttr) {
          combinedAttr.name = combinedAttr.dataset.baseName;
          combinedAttr.value = detailsString;
          combinedAttr.disabled = false;
        }

        // Customer-visible property on the twin kit: "Original Size: X -> New Size: Y"
        // The name is set from data-res-visible-base-name (e.g.
        // `properties[Resizing (Ring A)]`) so loxPropsToCleanFromParent can
        // derive the exact key from the service's `Resizing For` property.
        if (visibleProp) {
          const baseName = visibleProp.dataset.resVisibleBaseName;
          if (baseName) visibleProp.name = baseName;
          visibleProp.value = detailsString;
          visibleProp.disabled = false;
        }
      } else {
        resBox.style.display = 'none';
        inputs.forEach((i) => (i.disabled = true));

        if (combinedAttr) {
          combinedAttr.value = '';
          combinedAttr.removeAttribute('name');
          combinedAttr.disabled = true;
        }
        if (baseSizeProp) {
          baseSizeProp.value = '';
          baseSizeProp.disabled = true;
        }
        if (sizeProp) {
          sizeProp.value = '';
          sizeProp.disabled = true;
        }

        // No resizing selected — remove name and clear value so the input
        // is not submitted to the cart.
        if (visibleProp) {
          visibleProp.removeAttribute('name');
          visibleProp.value = '';
          visibleProp.disabled = true;
        }
      }
    }

    const noteInput = container.querySelector('.bundle-order-note-input');
    if (noteInput) noteInput.value = '';

    // Refresh the button state after EVERY change.
    refreshSubmitState();
  }

  // Click on a resizing option (size with adjustment).
  container.querySelectorAll('.bundle-resizable-span').forEach((span) => {
    span.addEventListener('click', function (e) {
      e.preventDefault();
      if (this.getAttribute('data-unavailable') === 'true') return;

      const itemContainer = this.closest('.bundle-item-container');
      const itemId = itemContainer.dataset.itemId;

      itemContainer
        .querySelectorAll('.bundle-resizable-span')
        .forEach((s) => s.classList.remove('active'));
      this.classList.add('active');

      const radio = document.getElementById(this.getAttribute('data-parent-id'));
      if (radio) {
        radio.checked = true;
        updateItemState(
          itemId,
          this.getAttribute('data-value'),
          this.getAttribute('data-base-value'),
          true
        );
      }
    });
  });

  // Click on a base size (no resizing).
  container.querySelectorAll('.bundle-label').forEach((label) => {
    label.addEventListener('click', function () {
      if (this.getAttribute('data-unavailable') === 'true') return;

      const itemContainer = this.closest('.bundle-item-container');
      const itemId = itemContainer.dataset.itemId;

      itemContainer
        .querySelectorAll('.bundle-resizable-span')
        .forEach((s) => s.classList.remove('active'));

      const radio = document.getElementById(this.getAttribute('for'));
      if (radio) {
        updateItemState(
          itemId,
          radio.getAttribute('data-base-size'),
          radio.getAttribute('data-base-size'),
          false
        );
      }
    });
  });
});